import React, { useCallback, useEffect, useRef } from 'react'
import { CaretRightIcon } from '@/atoms/Icons/CaretRightIcon'
import { Image } from '@/atoms/Image'
import { InternalLink } from '@/atoms/InternalLink'
import { PlayButtonThumbnailOverlay } from '@/atoms/PlayButtonThumbnailOverlay'
import { CaptionSM } from '@/atoms/Text'
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver'
import { ProgressOverlay } from '@/molecules/MediaCard/ProgressOverlay'
import {
  BrandLandingTabItemInteraction,
  useContentInteraction,
  HOVER_THRESHOLD_MS,
} from '@/services/Analytics/ContentInteractions'
import { VideoMetaData } from '@/services/ProjectsService'
import { useTranslate } from '@/utils/translate/translate-client'

export interface BonusVideoCardProps {
  video: VideoMetaData
  projectSlug: string
  itemIndex: number
  pagePlacement: number
  durationLabel?: string
  onVideoClick?: (video: VideoMetaData) => void
}

export const BonusVideoCard: React.FC<BonusVideoCardProps> = ({
  video,
  projectSlug,
  itemIndex,
  pagePlacement,
  durationLabel,
  onVideoClick,
}) => {
  const { t } = useTranslate('watch')
  const { trackInteraction } = useContentInteraction()
  const [cardRef, isVisible] = useIntersectionObserver<HTMLDivElement>({
    threshold: 0.9, // Track when at least 90% of the card is visible
  })

  // Hover tracking state
  const hoverStartTimeRef = useRef<number | null>(null)
  const impressionTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const hasTrackedImpression = useRef(false)

  const videoId = video.guid
  const videoThumbnailPath = video.posterLandscapeCloudinaryPath
  const href = `/watch/${projectSlug}/videos/${video.page}/${video.guid}`

  let progress = 0
  if (video?.watchPosition?.position && video.source?.duration) {
    progress = (video?.watchPosition?.position / video?.source.duration) * 100
  }

  const handleMouseEnter = useCallback(() => {
    if (!videoId) return

    // Record the start time
    hoverStartTimeRef.current = Date.now()
  }, [videoId])

  const handleMouseLeave = useCallback(() => {
    if (!videoId || !hoverStartTimeRef.current) return

    const dwellTime = Date.now() - hoverStartTimeRef.current

    // Only track hover if the user hovered for at least the threshold time
    if (dwellTime >= HOVER_THRESHOLD_MS && videoThumbnailPath) {
      const interaction = new BrandLandingTabItemInteraction({
        id: videoId,
        __typename: 'ContentLivestream',
        attribution: {
          itemIndex,
          pagePlacement,
          image: {
            cloudinaryPath: videoThumbnailPath,
          },
        },
      })
        .withSurface('brand_landing')
        .withPlacement('extras_tab', pagePlacement, itemIndex, 'vertical_list')
        .withImageArtwork(videoThumbnailPath, 'landscape_display')
        .withHover(dwellTime)

      trackInteraction(interaction)
    }

    // Reset state
    hoverStartTimeRef.current = null
  }, [videoId, videoThumbnailPath, pagePlacement, itemIndex, trackInteraction])

  const handleClick = useCallback(() => {
    if (onVideoClick) {
      onVideoClick(video)
    }

    if (!videoId || !videoThumbnailPath) return

    const interaction = new BrandLandingTabItemInteraction({
      id: videoId,
      __typename: 'ContentLivestream',
      attribution: {
        itemIndex,
        pagePlacement,
        image: {
          cloudinaryPath: videoThumbnailPath,
        },
      },
    })
      .withSurface('brand_landing')
      .withPlacement('extras_tab', pagePlacement, itemIndex, 'vertical_list')
      .withImageArtwork(videoThumbnailPath, 'landscape_display')
      .withPress()

    trackInteraction(interaction)
  }, [videoId, videoThumbnailPath, pagePlacement, itemIndex, trackInteraction, onVideoClick, video])

  // Track impression when the card becomes visible for at least 1 second
  useEffect(() => {
    if (!videoId || hasTrackedImpression.current) return

    if (isVisible) {
      // Start a timer to track impression after 1 second
      impressionTimeoutRef.current = setTimeout(() => {
        hasTrackedImpression.current = true

        if (videoThumbnailPath) {
          const interaction = new BrandLandingTabItemInteraction({
            id: videoId,
            __typename: 'ContentLivestream',
            attribution: {
              itemIndex,
              pagePlacement,
              image: {
                cloudinaryPath: videoThumbnailPath,
              },
            },
          })
            .withSurface('brand_landing')
            .withPlacement('extras_tab', pagePlacement, itemIndex, 'vertical_list')
            .withImageArtwork(videoThumbnailPath, 'landscape_display')
            .withImpression()

          trackInteraction(interaction)
        }
      }, 1000) // Wait 1 second before tracking impression
    } else {
      // Clear timeout if item becomes not visible before 1 second
      if (impressionTimeoutRef.current) {
        clearTimeout(impressionTimeoutRef.current)
        impressionTimeoutRef.current = null
      }
    }

    return () => {
      if (impressionTimeoutRef.current) {
        clearTimeout(impressionTimeoutRef.current)
      }
    }
  }, [isVisible, videoId, videoThumbnailPath, trackInteraction, itemIndex, pagePlacement])

  return (
    <div
      ref={cardRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className="flex w-full flex-row items-start gap-4"
    >
      <div className="shrink-0 snap-center snap-always">
        <InternalLink href={href} className="relative cursor-pointer" onClick={handleClick}>
          <Image
            alt={`${video.title} Bonus Content Thumbnail`}
            src={videoThumbnailPath || ''}
            className="h-full max-h-[82px] w-full max-w-[146px] rounded-lg lg:max-h-[177px] lg:max-w-[316px]"
            width={316}
            height={177}
          />
          <PlayButtonThumbnailOverlay />
          <ProgressOverlay progress={progress} />
        </InternalLink>
      </div>
      <div className="space-between flex h-full w-full flex-row items-center gap-x-4 lg:items-start lg:gap-x-0">
        <div className="flex grow-1 flex-col gap-[6px]">
          <CaptionSM color="core-gray-500" className="md:photon-caption-md lg:photon-caption-lg">
            {t('bonus', 'Bonus')}
          </CaptionSM>
          <CaptionSM weight="bold" className="lg:photon-title-md line-clamp-2 lg:line-clamp-4">
            {video.subtitle}
          </CaptionSM>
          {durationLabel && (
            <CaptionSM color="core-gray-500" className="md:photon-paragraph-md">
              {durationLabel}
            </CaptionSM>
          )}
        </div>
        <a className="lg:hidden" href={href}>
          <CaretRightIcon size={24} color="white" />
        </a>
      </div>
    </div>
  )
}
